/* Turn a BlockTree into a GeoJson document */

import type { BlockTree } from "@/blocks/block-tree";
import type { RenderedBlock } from "@/blocks/blocks";
import { GeoJSONSchema } from "zod-geojson";

import type {
  FeatureBlock,
  MultiPointBlock,
  PointBlock,
  RemoteGeoJSONBlock,
} from "@/blocks/geojson.blocks";
import {
  NestedFeatureError,
  UnsupportedBlockTypeError,
} from "@/blocks/geojson.errors";
import type { RouteBlock } from "./routes.blocks";
import type { Point } from "geojson";

function uuidToInteger(uuid: string): number {
  // Mapbox feature ID must be an integer or string that can be casted to an integer
  // Note the the GeoJSON spec does not have this requirement
  // https://datatracker.ietf.org/doc/html/rfc7946#section-3.2
  //   >  If a Feature has a commonly used identifier, that identifier
  //     SHOULD be included as a member of the Feature object with the name
  //     "id", and the value of this member is either a JSON string or
  //     number.
  // First 13 digits the UUID in hexadecimal equals 52 bits of randomness
  // Probability of id collision at 100k features is 1 in 56.000
  return parseInt(uuid.replace(/-/g, "").slice(0, 13), 16);
}

function innerToGeoJSON(
  block: RenderedBlock,
  tree: BlockTree,
): GeoJSON.Geometry | GeoJSON.Feature | GeoJSON.Feature[] {
  switch (block.type) {
    case "point":
      const pointBlock = block as PointBlock;
      return {
        type: "Point",
        coordinates: pointBlock.attributes.coordinates,
      };

    case "multipoint":
      const multiPointBlock = block as MultiPointBlock;
      return {
        type: "MultiPoint",
        coordinates: multiPointBlock.attributes.coordinates,
      };

    case "route":
      const routeBlock = block as RouteBlock;
      const children = tree.getBlockTree(routeBlock.id);
      const features: GeoJSON.Feature[] = [];

      for (const child of children) {
        const result = innerToGeoJSON(child, tree);
        if (Array.isArray(result)) {
          // Handle nested routes that return arrays of features
          features.push(...result);
        } else if (result.type === "Feature") {
          features.push(result);
        }
        // Ignore plain geometries since routes should only contain Features
      }
      // Create LineString Feature to connect the route waypoints
      const lineStringCoords = features
        .filter((feature) => feature.geometry?.type === "Point")
        .map((feature) => (feature.geometry as Point).coordinates);

      if (lineStringCoords.length > 1) {
        features.push({
          type: "Feature",
          id: uuidToInteger(routeBlock.id),
          geometry: {
            type: "LineString",
            coordinates: lineStringCoords,
          },
          properties: {},
        });
      }
      return features;

    case "feature":
      const featureBlock = block as FeatureBlock;
      const geomBlocks = tree.getBlockTree(featureBlock.id);

      // Sanity check
      if (geomBlocks.length > 1) {
        console.warn(
          `Feature ${featureBlock.id} has multiple children, only the first will be considered`,
        );
      }
      let geometry;
      if (geomBlocks[0]) {
        geometry = innerToGeoJSON(geomBlocks[0], tree);
        if (!Array.isArray(geometry) && geometry.type === "Feature") {
          throw new NestedFeatureError();
        }
      } else {
        geometry = null;
      }

      return {
        type: "Feature",
        id: uuidToInteger(featureBlock.id),
        // NOTE: geometry can be null according to spec, but not according to the types.
        geometry: geometry as GeoJSON.Geometry,
        properties: featureBlock.attributes.properties,
      };
    case "remotegeojson":
      const remoteGeoJsonBlock = block as RemoteGeoJSONBlock;
      if (
        remoteGeoJsonBlock.attributes.cache &&
        remoteGeoJsonBlock.attributes.cache.data
      ) {
        const parsedData = GeoJSONSchema.safeParse(
          remoteGeoJsonBlock.attributes.cache.data,
        );
        if (parsedData.success) {
          return parsedData.data.features as GeoJSON.Feature[];
        }
      }
      return [];

    case "pending":
      return [];

    default:
      throw new UnsupportedBlockTypeError(block.type);
  }
}

function geomToPlainFeature(geom: GeoJSON.Geometry): GeoJSON.Feature {
  return {
    type: "Feature",
    id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
    geometry: geom,
    properties: {
      "map::label": "Point",
    },
  };
}

export function GeoJsonView(root: BlockTree): GeoJSON.FeatureCollection {
  const rootBlocks = root.getDocumentRoot();
  const allFeatures: GeoJSON.Feature[] = [];

  for (const block of rootBlocks) {
    const result = innerToGeoJSON(block, root);

    if (Array.isArray(result)) {
      // Handle arrays of features from routes
      allFeatures.push(...result);
    } else {
      // Handle single geometries or features
      let feature: GeoJSON.Feature;
      if (result.type === "Point" || result.type === "MultiPoint") {
        // Top level geometry like Points can be wrapped into a Feature for convenience
        feature = geomToPlainFeature(result);
      } else if (result.type === "Feature") {
        feature = result;
      } else {
        // Skip other geometry types that aren't wrapped in Features
        continue;
      }
      allFeatures.push(feature);
    }
  }

  return {
    type: "FeatureCollection",
    features: allFeatures,
  };
}
